/**
 * Les briques de la cascade du lecteur de forme (`lecteurDeForme.ts`) : lire un
 * attribut Word, lire un réglage à un niveau, et ce qu'est un niveau.
 *
 * WHY un module à part : le lecteur et ses résolutions voisines — police de
 * thème, numérotation, style de tableau — lisent le même XML avec les mêmes
 * gestes. Les garder dans le lecteur obligerait ces voisins à l'importer, lui
 * qui les importe déjà : ce module n'importe donc rien du lecteur.
 */

import { isWord, type XmlNode } from '@hector/docx-integrity';
import type { CanvasFormatOrigin } from '@hector/shared-types';

/** Attribut `w:` d'un élément `w:` : il vit dans l'espace de noms de son élément (transitional ou strict). */
export function attribut(noeud: XmlNode | undefined, nom: string): string | undefined {
  return noeud?.attributes[`{${noeud.uri}}${nom}`];
}

/** Premier enfant `w:nom` d'un élément ; `undefined` s'il n'en a pas, ou si l'élément lui-même manque. */
export function enfant(noeud: XmlNode | undefined, nom: string): XmlNode | undefined {
  return noeud?.children.find((candidat) => isWord(candidat, nom));
}

/**
 * Jeton on/off de Word (`ST_OnOff`) : `0`, `false`, `off` éteignent, toute
 * autre valeur allume. L'attribut ABSENT n'a pas de sens unique — présent sans
 * valeur, `<w:b/>` allume ; absent, `w:default` éteint — d'où `siAbsent`.
 */
export function allume(valeur: string | undefined, siAbsent: boolean): boolean {
  if (valeur === undefined) return siAbsent;
  return !(valeur === '0' || valeur === 'false' || valeur === 'off');
}

/** Pourquoi un réglage n'est pas rendu. Chaque raison nomme la part de la cascade que le lecteur ne résout pas. */
export type RaisonNonLisible =
  /** Police de thème sans nom lisible dans `theme1.xml`, ou nommée autrement par le même élément (l'éditeur et la norme divergent). */
  | 'police_de_theme'
  /** Le paragraphe est numéroté par une liste, ou un niveau, que le fichier ne définit pas : retraits, alignement et espacement en dépendent. */
  | 'numerotation'
  /** Formats conditionnels du style de tableau dont l'application à la cellule n'est pas établie : contradiction, bandes, tableau à cellules fusionnées ou décalées. */
  | 'style_de_tableau'
  /** Le style nommé du paragraphe, ou le style de caractère du passage, n'est pas défini dans le fichier. */
  | 'style_introuvable'
  /** Passage en exposant ou en indice : l'éditeur le peint plus petit que la taille que porte le fichier. */
  | 'exposant_ou_indice'
  /** Passage déplacé par une révision en attente : l'éditeur le peint souligné, quelle que soit sa forme. */
  | 'revision_de_deplacement'
  /** Interligne fixé en points (`w:lineRule` « exact » ou « atLeast ») : il ne s'exprime pas en multiple de l'interligne simple. */
  | 'interligne_en_points'
  /** Les côtés tracés d'une bordure de paragraphe n'ont pas la même épaisseur : aucune valeur ne la dit. */
  | 'epaisseurs_differentes'
  /** Le fichier porte une valeur que ce lecteur ne sait pas traduire. */
  | 'valeur_inconnue'
  /** Aucun niveau ne définit le réglage, et Word n'en fixe pas de défaut sûr (police, taille). */
  | 'non_defini';

/** Ce qu'un niveau de la cascade dit d'un réglage. `undefined` : ce niveau n'en dit rien, on descend. */
export type Lu<Valeur> = { valeur: Valeur } | { nonLisible: RaisonNonLisible } | undefined;
/** La lecture d'UN réglage dans un porteur de propriétés (`w:pPr` ou `w:rPr`) ; le porteur peut manquer. */
export type Lecture<Valeur> = (proprietes: XmlNode | undefined) => Lu<Valeur>;

/**
 * Un niveau de la cascade, du plus fort (forme directe) au plus faible (défauts
 * du document).
 *
 * Le plus souvent un niveau est UN porteur de propriétés (`w:pPr` ou `w:rPr`).
 * `arbitre` est le niveau qui en réunit plusieurs sans ordre établi entre eux —
 * les formats conditionnels d'un style de tableau : il répond lui-même à
 * chaque lecture, et dit « non lisible » quand ses porteurs se contredisent.
 */
export type Niveau =
  | { sorte: 'porteur'; origine: CanvasFormatOrigin; proprietes: XmlNode | undefined }
  | { sorte: 'arbitre'; origine: CanvasFormatOrigin; arbitre: <Valeur>(lire: Lecture<Valeur>) => Lu<Valeur> };

/** Le niveau d'un seul porteur de propriétés. */
export function porteur(origine: CanvasFormatOrigin, proprietes: XmlNode | undefined): Niveau {
  return { sorte: 'porteur', origine, proprietes };
}

/** Ce que `niveau` dit du réglage que lit `lire`. */
export function lireAuNiveau<Valeur>(niveau: Niveau, lire: Lecture<Valeur>): Lu<Valeur> {
  return niveau.sorte === 'arbitre' ? niveau.arbitre(lire) : lire(niveau.proprietes);
}
